import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const DriverModal = ({ isOpen, onClose, driver, onSave }) => {
    const { isDark } = useTheme();
    const [cars, setCars] = useState([]);
    const [potentialDrivers, setPotentialDrivers] = useState([]);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        status: 'ACTIVE',
        car_id: '',
        address: '',
        driver_documents_link: '',
        sales_day: '',
        potential_driver_id: '',
    });
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchCars();
            fetchPotentialDrivers();
            if (driver) {
                setFormData({
                    first_name: driver.first_name || '',
                    last_name: driver.last_name || '',
                    phone_number: driver.phone_number || '',
                    email: driver.email || '',
                    status: driver.status || 'ACTIVE',
                    car_id: driver.car_id || '',
                    address: driver.address || '',
                    driver_documents_link: driver.driver_documents_link || '',
                    slack_web_hook_url: driver.slack_web_hook_url || '',
                    sales_day: driver.sales_day || '',
                });
            } else {
                setFormData({
                    first_name: '',
                    last_name: '',
                    phone_number: '',
                    email: '',
                    status: 'ACTIVE',
                    car_id: '',
                    address: '',
                    driver_documents_link: '',
                    slack_web_hook_url: '',
                    sales_day: '',
                });
            }
            setErrors([]);
        }
    }, [isOpen, driver]);

    const fetchCars = async () => {
        try {
            const response = await fetch('/api/v1/drivers/cars_list', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setCars(data);
        } catch (error) {
            console.error('Error fetching cars:', error);
        }
    };

    const fetchPotentialDrivers = async () => {
        try {
            const response = await fetch('/api/v1/drivers/potential_drivers_list', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setPotentialDrivers(data);
        } catch (error) {
            console.error('Error fetching potential drivers:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePotentialDriverChange = async (e) => {
        const potentialDriverId = e.target.value;
        setFormData(prev => ({
            ...prev,
            potential_driver_id: potentialDriverId
        }));

        if (potentialDriverId) {
            try {
                const response = await fetch(`/potential_drivers/${potentialDriverId}.json`);
                const pd = await response.json();
                setFormData(prev => ({
                    ...prev,
                    first_name: pd.first_name || '',
                    last_name: pd.last_name || '',
                    phone_number: pd.phone_number || '',
                    email: pd.email || '',
                    address: pd.address || '',
                    potential_driver_id: potentialDriverId
                }));
            } catch (error) {
                console.error('Error fetching potential driver details:', error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);

        try {
            const url = driver ? `/api/v1/drivers/${driver.id}` : '/api/v1/drivers';
            const method = driver ? 'PATCH' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                body: JSON.stringify({ driver: formData }),
            });

            const data = await response.json();

            if (response.ok) {
                onSave(data);
                onClose();
            } else {
                setErrors(data.errors || ['An error occurred']);
            }
        } catch (error) {
            console.error('Error saving driver:', error);
            setErrors(['Failed to save driver. Please try again.']);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        },
        header: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
            color: isDark ? '#ffffff' : '#1f2937',
        },
        formGroup: {
            marginBottom: '1.5rem',
        },
        label: {
            display: 'block',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isDark ? '#d1d5db' : '#374151',
        },
        input: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        select: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        formRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem',
        },
        errorList: {
            background: isDark ? '#7f1d1d' : '#fee2e2',
            color: isDark ? '#fca5a5' : '#991b1b',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            marginTop: '2rem',
        },
        saveButton: {
            flex: 1,
            padding: '0.75rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
        cancelButton: {
            flex: 1,
            padding: '0.75rem',
            background: isDark ? '#374151' : '#e5e7eb',
            color: isDark ? '#d1d5db' : '#374151',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={styles.header}>{driver ? 'Edit Driver' : 'New Driver'}</h2>

                {errors.length > 0 && (
                    <div style={styles.errorList}>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {!driver && (
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Select Potential Driver (Optional)</label>
                            <select
                                name="potential_driver_id"
                                value={formData.potential_driver_id}
                                onChange={handlePotentialDriverChange}
                                style={styles.select}
                            >
                                <option value="">-- Select to auto-fill details --</option>
                                {potentialDrivers.map(pd => (
                                    <option key={pd.id} value={pd.id}>{pd.label}</option>
                                ))}
                            </select>
                            <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '0.25rem' }}>
                                Selecting a potential driver will auto-fill their information below
                            </div>
                        </div>
                    )}

                    <div style={styles.formRow}>
                        <div>
                            <label style={styles.label}>First Name *</label>
                            <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="John"
                            />
                        </div>
                        <div>
                            <label style={styles.label}>Last Name *</label>
                            <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="Doe"
                            />
                        </div>
                    </div>

                    <div style={styles.formRow}>
                        <div>
                            <label style={styles.label}>Phone Number *</label>
                            <input
                                type="tel"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="+233123456789"
                            />
                        </div>
                        <div>
                            <label style={styles.label}>Email *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="john@example.com"
                            />
                        </div>
                    </div>

                    <div style={styles.formRow}>
                        <div>
                            <label style={styles.label}>Status *</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                required
                                style={styles.select}
                            >
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="INACTIVE">INACTIVE</option>
                                <option value="CONTRACT_TERMINATED">CONTRACT_TERMINATED</option>
                            </select>
                        </div>
                        <div>
                            <label style={styles.label}>Car *</label>
                            <select
                                name="car_id"
                                value={formData.car_id}
                                onChange={handleChange}
                                required
                                style={styles.select}
                            >
                                <option value="">Select car...</option>
                                {cars.map(car => (
                                    <option key={car.id} value={car.id}>{car.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Address</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="123 Main Street"
                        />
                    </div>

                    <div style={styles.formRow}>
                        <div>
                            <label style={styles.label}>Driver Documents Link</label>
                            <input
                                type="url"
                                name="driver_documents_link"
                                value={formData.driver_documents_link}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="https://..."
                            />
                        </div>
                        <div>
                            <label style={styles.label}>Slack Webhook URL</label>
                            <input
                                type="url"
                                name="slack_web_hook_url"
                                value={formData.slack_web_hook_url}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="https://hooks.slack.com/..."
                            />
                        </div>
                    </div>

                    <div style={styles.formRow}>
                        <div>
                            <label style={styles.label}>Sales Day</label>
                            <select
                                name="sales_day"
                                value={formData.sales_day}
                                onChange={handleChange}
                                style={styles.select}
                            >
                                <option value="">Select day...</option>
                                <option value="Monday">Monday</option>
                                <option value="Tuesday">Tuesday</option>
                                <option value="Wednesday">Wednesday</option>
                                <option value="Thursday">Thursday</option>
                                <option value="Friday">Friday</option>
                                <option value="Saturday">Saturday</option>
                                <option value="Sunday">Sunday</option>
                            </select>
                        </div>
                    </div>

                    <div style={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            style={styles.saveButton}
                        >
                            {loading ? 'Saving...' : (driver ? 'Update Driver' : 'Create Driver')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DriverModal;
